/**
 * El demo local (2026-09-18, decisión de Santiago: «demo local, sin servidor»).
 *
 * «Probar el demo» en el login abre la caja con un negocio de demostración que vive entero en este navegador: las
 * peticiones a los servicios del POS las contesta `demoInterceptor` aquí mismo y ninguna sale a la red. Antes, un
 * dispositivo nuevo que abría `/pos` caía en una sesión «Demo» que el POS se inventaba sin token: sin servidor, la
 * caja salía vacía y no podía vender (medido el 18/09). Eso ya no pasa: sin sesión se va al login.
 *
 * Alcance (ECM): la caja, más cliente con cupo y venta a crédito. Sin historial, cierre, pedidos, Ruta ni cartera:
 * cada pantalla más es otro servidor falso que mantener.
 */

/** El negocio del demo. Distinto de cualquier negocio real y del «demo» que usan de respaldo algunos servicios. */
export const NEGOCIO_DEL_DEMO = 'demo-en-este-equipo';

/** Marca de «estás en el demo». Mientras exista, `demoInterceptor` contesta y nada sale a la red. */
export const CLAVE_DEL_DEMO = 'suresell.demo';

/** Nombre que se ve en la barra lateral (texto de ECM). */
export const NOMBRE_DEL_NEGOCIO_DEL_DEMO = 'Negocio de demostración';

/** Los módulos del demo (ECM): vender, y el mayorista para cliente, cupo y crédito. */
export const MODULOS_DEL_DEMO = ['ventas', 'mayorista'] as const;

/** ¿Está este navegador en el demo? Si el almacenamiento no se puede leer, no: nunca se contesta a ciegas. */
export function estaEnElDemo(): boolean {
  try {
    return localStorage.getItem(CLAVE_DEL_DEMO) === '1';
  } catch {
    return false;
  }
}

/** Solo ASCII: lo que se codifica aquí son claves y valores fijos, sin tildes. */
const base64Url = (texto: string): string =>
  btoa(texto).replace(/=+$/, '').replace(/\+/g, '-').replace(/\//g, '_');

/**
 * Un token CON FORMA de JWT y sin firma. Hace falta porque el POS decide el negocio leyendo `tenant_id` del token
 * (catálogo, perfil, cola de ventas) y varios servicios no preguntan nada sin él. Nunca llega a un servidor:
 * `demoInterceptor` contesta aquí todas las peticiones de otro origen mientras dure el demo. Si la marca
 * se perdiera y el token no, el servidor real lo rechazaría (401) y la caja cerraría la sesión: sin daño.
 */
export function tokenDelDemo(): string {
  return `${base64Url(JSON.stringify({ alg: 'none', typ: 'JWT' }))}.${base64Url(
    JSON.stringify({ tenant_id: NEGOCIO_DEL_DEMO, sub: 'demo', role: 'cajero', demo: true }),
  )}.sin-firma`;
}
